import { useMemo } from 'react';
import type { ReactElement } from 'react';
import type { AirportView, SlotAuctionAirportView } from '../../../domain/models/contracts';
import { formatInt, formatUsd } from '../analytics/formatters';
import { InfoPopover } from '../common/InfoPopover';
import './MarketSlotAuctions.css';

export interface MarketSlotAuctionsProps {
  /** Slot auction results from the latest competitor cycle. */
  readonly auctions: readonly SlotAuctionAirportView[];
  /** Sim day the auctions cleared on; 0 before the first cycle. */
  readonly day: number;
  readonly airports: readonly AirportView[];
}

function formatHour(hourUtc: number): string {
  return `${String(hourUtc).padStart(2, '0')}:00`;
}

function isActiveHour(hour: SlotAuctionAirportView['hours'][number]): boolean {
  return hour.requested > 0 || hour.awarded > 0;
}

/**
 * Read-only view of slot-constrained airports. Each card shows what the
 * airport's auction awarded, denied and charged per hour. Clearing prices are
 * uniform: every winner in an hour pays the same annual price.
 */
export function MarketSlotAuctions({ auctions, day, airports }: MarketSlotAuctionsProps): ReactElement {
  const nameById = useMemo<ReadonlyMap<string, string>>(
    () => new Map(airports.map((airport) => [airport.id, airport.name] as const)),
    [airports],
  );

  return (
    <section className="msa" aria-label="Market and slot auctions">
      <header className="msa-header">
        <div>
          <h2 className="msa-title">
            Market &amp; Slot Auctions
            <InfoPopover topic="competitorYieldResistance" />
          </h2>
          <p className="msa-subtitle">
            {day > 0
              ? `Latest competitor cycle, day ${day}. Slot-constrained airports only.`
              : 'Waiting for the first competitor cycle.'}
          </p>
        </div>
      </header>

      {auctions.length === 0 ? (
        <p className="msa-empty">
          No slot-constrained airport was contested this cycle. Auctions run during the daily
          competitor cycle when carriers request slots.
        </p>
      ) : (
        auctions.map((auction) => {
          const activeHours = auction.hours.filter(isActiveHour);
          return (
            <article key={auction.airportId} className="msa-airport">
              <header className="msa-airport__head">
                <h3 className="msa-airport__name">
                  {nameById.get(auction.airportId) ?? auction.airportId}
                  <span className="msa-airport__code">{auction.airportId}</span>
                </h3>
              </header>

              <dl className="msa-stats">
                <div className="msa-stat">
                  <dt>Slots awarded</dt>
                  <dd>{formatInt(auction.slotsAwarded)}</dd>
                </div>
                <div className="msa-stat">
                  <dt>Slots remaining</dt>
                  <dd>{formatInt(auction.slotsRemaining)}</dd>
                </div>
                <div className="msa-stat">
                  <dt>Requests denied</dt>
                  <dd>{formatInt(auction.deniedRequests)}</dd>
                </div>
                <div className="msa-stat">
                  <dt>Licence revenue</dt>
                  <dd>{formatUsd(auction.totalLicenseRevenueUSD)}</dd>
                </div>
              </dl>

              {activeHours.length === 0 ? (
                <p className="msa-empty">No hour at this airport had requests or awards.</p>
              ) : (
                <div className="msa-table-wrap">
                  <table className="msa-table">
                    <thead>
                      <tr>
                        <th scope="col">Hour (UTC)</th>
                        <th scope="col" className="msa-num">
                          Capacity
                        </th>
                        <th scope="col" className="msa-num">
                          Committed
                        </th>
                        <th scope="col" className="msa-num">
                          Requested
                        </th>
                        <th scope="col" className="msa-num">
                          Awarded
                          <InfoPopover topic="grandfatherRights" />
                        </th>
                        <th scope="col" className="msa-num">
                          Reserve / slot
                        </th>
                        <th scope="col" className="msa-num">
                          Clearing / slot
                          <InfoPopover topic="slotClearingPrice" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeHours.map((hour) => {
                        const oversubscribed = hour.requested > hour.awarded;
                        return (
                          <tr key={hour.hourUtc} data-oversubscribed={oversubscribed ? 'true' : 'false'}>
                            <th scope="row">{formatHour(hour.hourUtc)}</th>
                            <td className="msa-num">{formatInt(hour.capacity)}</td>
                            <td className="msa-num">{formatInt(hour.committed)}</td>
                            <td className="msa-num">{formatInt(hour.requested)}</td>
                            <td className="msa-num">{formatInt(hour.awarded)}</td>
                            <td className="msa-num">{formatUsd(hour.reservePriceUSD)}</td>
                            <td className="msa-num msa-price">{formatUsd(hour.clearingPriceUSD)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          );
        })
      )}
    </section>
  );
}
